import { EventFilterValue, GPEventResponse, ProcessingLevelEnum } from "../../types";

export interface TeamStatCalculationResult {
	isGoal: boolean,
	label: string,
	description: string,
	homeGameTeamId: number,
	awayGameTeamId: number,
	homeEvents: GPEventResponse[],
	awayEvents: GPEventResponse[],
	allEvents: GPEventResponse[],
	homeValue: number,
	homeValueFormatted: string,
	awayValueFormatted: string,
	awayValue: number,
	allValue: number,
	filterValues: EventFilterValue[],
	isFilterable: boolean,
	homeGoalEvents?: GPEventResponse[],
	awayGoalEvents?: GPEventResponse[],
	homeOwnGoalEvents?: GPEventResponse[],
	awayOwnGoalEvents?: GPEventResponse[],
	goalFilterValues?: EventFilterValue[],
	ownGoalFilterValues?: EventFilterValue[],
	isDescriptionOnly?: boolean,
}

export enum CategoryEnum {
	Summary = "summary",
	Offensive = "offensive",
	Defensive = "defensive",
	Distribution = "distribution",
	AdvancedMetrics = "advancedMetrics",
}

export enum TeamStatType {
	AerialDuel = "aerialDuel",
	AverageRating = "averageRating",
	Block = "block",
	Clearance = "clearance",
	Corner = "corner",
	Cross = "cross",
	CrossSuccess = "crossSuccess",
	Foul = "foul",
	FoulWon = "foulWon",
	FreeKick = "freeKick",
	Goal = "goal",
	GoalOwn = "goalOwn",
	Interception = "interception",
	KeyPass = "keyPass",
	Mistake = "mistake",
	Offside = "offside",
	Pass = "pass",
	PassAccuracy = "passAccuracy",
	PassBackward = "passBackward",
	PassBackwardSuccess = "passBackwardSuccess",
	PassForward = "passForward",
	PassForwardSuccess = "passForwardSuccess",
	PassInDefensiveThird = "passInDefensiveThird",
	PassInDefensiveThirdSuccess = "passInDefensiveThirdSuccess",
	PassInFinalThird = "passInFinalThird",
	PassInFinalThirdSuccess = "passInFinalThirdSuccess",
	PassInMiddleThird = "passInMiddleThird",
	PassInMiddleThirdSuccess = "passInMiddleThirdSuccess",
	PassLong = "passLong",
	PassLongSuccess = "passLongSuccess",
	PassMedium = "passMedium",
	PassMediumSuccess = "passMediumSuccess",
	PassShort = "passShort",
	PassShortSuccess = "passShortSuccess",
	PassSideways = "passSideways",
	PassSidewaysSuccess = "passSidewaysSuccess",
	PassSuccess = "passSuccess",
	Possession = "possession",
	Recovery = "recovery",
	Shot = "shot",
	ShotBlocked = "shotBlocked",
	ShotInsidePa = "shotInsidePa",
	ShotOffTarget = "shotOffTarget",
	ShotOnTarget = "shotOnTarget",
	ShotOutsidePa = "shotOutsidePa",
	Tackle = "tackle",
	TackleSuccess = "tackleSuccess",
	TakeOn = "takeOn",
	TakeOnSuccess = "takeOnSuccess",
	SecondBallWon = "secondBallWon",
	FieldTilt = "fieldTilt",
	ShotConversion = "shotConversion",
	TotalXg = "totalXg",
	TotalXa = "totalXa",
	NonPenaltyXg = "nonPenaltyXg",
	XgPerShot = "xgPerShot",
	ShotCreatingAction = "shotCreatingAction",
	PassIntoFinalThird = "passIntoFinalThird",
	PassIntoPenaltyArea = "passIntoPenaltyArea",
	ProgressivePass = "progressivePass",
	DeepCompletion = "deepCompletion",
	LongBallShare = "longBallShare",
}

export const expectedGoalsStatTypes = new Set<TeamStatType>([
	TeamStatType.TotalXg,
	TeamStatType.TotalXa,
	TeamStatType.NonPenaltyXg,
	TeamStatType.XgPerShot,
]);

const teamStatDefinition: Record<CategoryEnum, TeamStatType[]> = {
	[CategoryEnum.Summary]: [
		TeamStatType.Goal,
		TeamStatType.TotalXg,
		TeamStatType.Possession,
		TeamStatType.Shot,
		TeamStatType.Foul,
		TeamStatType.Offside,
		TeamStatType.FreeKick,
		TeamStatType.Corner,
		TeamStatType.AverageRating,
	],
	[CategoryEnum.Offensive]: [
		TeamStatType.Shot,
		TeamStatType.ShotOnTarget,
		TeamStatType.ShotOffTarget,
		TeamStatType.ShotBlocked,
		TeamStatType.ShotInsidePa,
		TeamStatType.ShotOutsidePa,
		TeamStatType.TakeOnSuccess,
		TeamStatType.TakeOn,
		TeamStatType.ShotConversion,
		TeamStatType.ShotCreatingAction,
	],
	[CategoryEnum.Defensive]: [
		TeamStatType.TackleSuccess,
		TeamStatType.Tackle,
		TeamStatType.AerialDuel,
		TeamStatType.Interception,
		TeamStatType.Clearance,
		TeamStatType.Recovery,
		TeamStatType.Block,
		TeamStatType.Mistake,
		TeamStatType.FoulWon,
		TeamStatType.SecondBallWon,
	],
	[CategoryEnum.Distribution]: [
		TeamStatType.PassSuccess,
		TeamStatType.Pass,
		TeamStatType.PassAccuracy,
		TeamStatType.KeyPass,
		TeamStatType.PassInFinalThirdSuccess,
		TeamStatType.PassInFinalThird,
		TeamStatType.PassInMiddleThirdSuccess,
		TeamStatType.PassInMiddleThird,
		TeamStatType.PassInDefensiveThirdSuccess,
		TeamStatType.PassInDefensiveThird,
		TeamStatType.PassLongSuccess,
		TeamStatType.PassLong,
		TeamStatType.PassMediumSuccess,
		TeamStatType.PassMedium,
		TeamStatType.PassShortSuccess,
		TeamStatType.PassShort,
		TeamStatType.PassForwardSuccess,
		TeamStatType.PassForward,
		TeamStatType.PassSidewaysSuccess,
		TeamStatType.PassSideways,
		TeamStatType.PassBackwardSuccess,
		TeamStatType.PassBackward,
		TeamStatType.CrossSuccess,
		TeamStatType.Cross,
		TeamStatType.FieldTilt,
		TeamStatType.PassIntoFinalThird,
		TeamStatType.PassIntoPenaltyArea,
		TeamStatType.ProgressivePass,
		TeamStatType.DeepCompletion,
		TeamStatType.LongBallShare,
	],
	[CategoryEnum.AdvancedMetrics]: [
		TeamStatType.TotalXg,
		TeamStatType.TotalXa,
		TeamStatType.NonPenaltyXg,
		TeamStatType.XgPerShot,
	],
}

const teamStatDefinitionLight: Record<CategoryEnum, TeamStatType[]> = {
	[CategoryEnum.Summary]: [
		TeamStatType.Goal,
		TeamStatType.Possession,
		TeamStatType.Shot,
		TeamStatType.Foul,
		TeamStatType.Offside,
		TeamStatType.FreeKick,
		TeamStatType.Corner,
	],
	[CategoryEnum.Offensive]: [
		TeamStatType.Shot,
		TeamStatType.ShotBlocked,
		TeamStatType.TakeOnSuccess,
		TeamStatType.TakeOn,
	],
	[CategoryEnum.Defensive]: [
		TeamStatType.TackleSuccess,
		TeamStatType.Tackle,
		TeamStatType.AerialDuel,
		TeamStatType.Interception,
		TeamStatType.Clearance,
		TeamStatType.Block,
		TeamStatType.Mistake,
		TeamStatType.FoulWon,

	],
	[CategoryEnum.Distribution]: [
		TeamStatType.Pass,
		TeamStatType.Cross,
		TeamStatType.KeyPass,
	],
	[CategoryEnum.AdvancedMetrics]: [],
}

export const getTeamStatCategories = (processingLevel?: ProcessingLevelEnum): CategoryEnum[] => {
	const isLimited = processingLevel === ProcessingLevelEnum.Limited;

	return Object.values(CategoryEnum).filter(
		(category) => isLimited === false || category !== CategoryEnum.AdvancedMetrics,
	);
}

export const getTeamStatDefinitionByCategory = (
	category: CategoryEnum,
	showRatings: boolean,
	processingLevel?: ProcessingLevelEnum,
	teamTagged?: string | null,
): TeamStatType[] => {
	const omitOneTeamTaggingStats = teamTagged !== undefined && teamTagged !== null && teamTagged !== "both";

	const shouldOmitStat = (statType: TeamStatType): boolean => {
		if (showRatings === false && statType === TeamStatType.AverageRating) {
			return true;
		}
		if (
			omitOneTeamTaggingStats
			&& (statType === TeamStatType.Possession
				|| statType === TeamStatType.TakeOn
				|| statType === TeamStatType.TakeOnSuccess
				|| statType === TeamStatType.Recovery
				|| statType === TeamStatType.FoulWon
				|| statType === TeamStatType.SecondBallWon
				|| statType === TeamStatType.FieldTilt)
		) {
			return true;
		}

		return false;
	};

	if (processingLevel === ProcessingLevelEnum.Limited) {
		const definition = teamStatDefinitionLight[category] ?? teamStatDefinition[category] ?? [];

		return definition.filter((statType) => shouldOmitStat(statType) === false);
	}

	const definition = teamStatDefinition[category] ?? [];

	return definition.filter((statType) => shouldOmitStat(statType) === false);
};
